"use client";

import * as React from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, Droplets, Flame, Gauge, PhoneCall, ShieldAlert, Wrench, Waves } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface ProblemItem {
  id: string;
  title: string;
  badge: string;
  description: string;
  commonSigns: string;
  link: string;
  linkText: string;
  urgency: "emergency" | "priority" | "routine";
  iconName: "droplets" | "flame" | "toilet" | "gauge" | "sewer" | "burst" | "clog";
}
const problemsList: ProblemItem[] = [{
  id: "clogs",
  title: "Slow and Blocked Drains",
  badge: "Common Issue",
  description: "Standing water in sinks, showers, or tubs caused by buildup of grease, hair, mineral scale, or foreign debris.",
  commonSigns: "Gurgling sounds, foul odors, slow water drainage",
  link: "/drain-problems/",
  linkText: "Drain Cleaning Solutions",
  urgency: "priority",
  iconName: "clog"
}, {
  id: "leaks",
  title: "Hidden and Active Water Leaks",
  badge: "Immediate Attention",
  description: "Unseen slab leaks, pipe pinholes, and dripping fixtures that drive up water bills and risk structural wood rot.",
  commonSigns: "Damp drywall, warm spots on floors, spinning water meter",
  link: "/water-leak-problems/",
  linkText: "Leak Detection Services",
  urgency: "emergency",
  iconName: "droplets"
}, {
  id: "hot-water",
  title: "No Hot Water and Heater Failure",
  badge: "High Demand",
  description: "Loss of hot water, lukewarm temperatures, or leaking tanks from faulty heating elements, thermostats, or sediment.",
  commonSigns: "Rusty water, popping sounds in tank, pilot light failure",
  link: "/water-heater-problems/",
  linkText: "Water Heater Repairs",
  urgency: "priority",
  iconName: "flame"
}, {
  id: "toilets",
  title: "Running and Overflowing Toilets",
  badge: "24/7 Dispatch",
  description: "Constant water running, weak flushes, or recurring clogs that threaten sanitation and waste hundreds of gallons daily.",
  commonSigns: "Phantom flushing, bowl filling slowly, base leaks",
  link: "/toilet-problems/",
  linkText: "Toilet Repair Services",
  urgency: "priority",
  iconName: "toilet"
}, {
  id: "pressure",
  title: "Sudden Low Water Pressure",
  badge: "Diagnostic Needed",
  description: "Weak shower streams and slow faucet fills caused by sediment accumulation, pressure regulators, or hidden mainline breaks.",
  commonSigns: "Trickling faucets, sputtering aerators, whole-home drop",
  link: "/water-pressure-problems/",
  linkText: "Pressure Diagnostics",
  urgency: "routine",
  iconName: "gauge"
}, {
  id: "sewer",
  title: "Main Sewer Line Backups",
  badge: "Hazardous",
  description: "Hazardous sewage backing up into floor drains or low-level fixtures due to tree root intrusion, collapsed pipes, or major blockages.",
  commonSigns: "Multiple blocked drains, raw sewage odors, soggy yard spots",
  link: "/sewer-line-repair/",
  linkText: "Sewer Line Restoration",
  urgency: "emergency",
  iconName: "sewer"
}, {
  id: "burst-pipes",
  title: "Burst and Cracked Pipes",
  badge: "Critical Emergency",
  description: "Ruptured water lines caused by pressure spikes, aging galvanized lines, or seasonal freezes requiring instant shutoff and repair.",
  commonSigns: "Rushing water sounds behind walls, sudden flooding, water pressure loss",
  link: "/emergency-plumbing-problems/",
  linkText: "Emergency Pipe Repair",
  urgency: "emergency",
  iconName: "burst"
}];
function ProblemIcon({
  type
}: {
  type: ProblemItem["iconName"];
}) {
  switch (type) {
    case "droplets":
      return <Droplets className="size-5 text-primary" />;
    case "flame":
      return <Flame className="size-5 text-destructive" />;
    case "toilet":
      return <Wrench className="size-5 text-primary" />;
    case "gauge":
      return <Gauge className="size-5 text-primary" />;
    case "sewer":
      return <ShieldAlert className="size-5 text-destructive" />;
    case "burst":
      return <AlertTriangle className="size-5 text-destructive" />;
    case "clog":
    default:
      return <Waves className="size-5 text-primary" />;
  }
}
export default function Problems() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="commonplumbingproblems" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border relative">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <Badge variant="secondary" className="px-3 py-1 font-medium text-xs tracking-wide uppercase">
              Local Diagnostics & Solutions
            </Badge>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
            Common Plumbing Problems in Forest Park
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            From sudden pipe bursts to slow kitchen clogs, plumbing issues disrupt your home fast. Review the common symptoms below and get direct assistance from our Forest Park licensed technicians.
          </p>
        </div>

        {/* Problems Grid */}
        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }}>
          {problemsList.map((item, index) => <motion.div key={item.id} data-index={index} variants={itemVariants} className="h-full flex">
              <Card className="w-full flex flex-col justify-between border-border/80 bg-card hover:border-primary/40 shadow-xs hover:shadow-md transition-all duration-200">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="size-10 rounded-lg bg-secondary flex items-center justify-center">
                      <ProblemIcon type={item.iconName} />
                    </div>
                    <Badge variant={item.urgency === "emergency" ? "destructive" : item.urgency === "priority" ? "default" : "secondary"} className="text-[11px]">
                      {item.badge}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl font-bold text-card-foreground">
                    {item.title}
                  </CardTitle>
                  <CardDescription className="text-sm text-muted-foreground leading-normal">
                    {item.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-2 text-xs">
                  <div className="bg-muted/60 p-3 rounded-md border border-border/60">
                    <span className="font-semibold text-foreground block mb-1">
                      Key Warning Signs:
                    </span>
                    <span className="text-muted-foreground">{item.commonSigns}</span>
                  </div>
                </CardContent>

                <CardFooter className="pt-2">
                  <Button asChild variant="outline" className="w-full justify-between hover:bg-primary hover:text-primary-foreground group transition-colors">
                    <Link to={item.link}>
                      <span>{item.linkText}</span>
                      <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Emergency Callout Box */}
        <div className="mt-12 rounded-xl bg-primary text-primary-foreground p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6 shadow-md">
          <div className="space-y-2 text-center sm:text-left">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider bg-primary-foreground/15 text-primary-foreground px-2.5 py-1 rounded-full">
              <PhoneCall className="size-3.5" /> 24/7 Forest Park Response
            </div>
            <h3 className="text-xl sm:text-2xl font-bold font-default">
              Facing an active leak or urgent sewage issue?
            </h3>
            <p className="text-sm text-primary-foreground/80 max-w-xl">
              Do not wait for water damage to spread. Our emergency response team in Jonesboro and Forest Park is available round the clock.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto shrink-0">
            <Button asChild variant="secondary" size="lg" className="w-full sm:w-auto font-semibold">
              <Link to="/contact/">
                Schedule Urgent Service
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
              <Link to="/problems/">
                View All Problems
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}